import { Employee } from "../../types/employee";
import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Space,
  Form,
  Switch, 
  Input, 
  Select, 
  Card, 
  Tag, 
  Avatar, 
  Typography, 
  message,
  Modal,
  Popconfirm,
  Tooltip
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  LockOutlined,
  BankOutlined,
  KeyOutlined, 
  EyeOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  FileTextOutlined 
} from '@ant-design/icons';
import apiService from "services/api/apiService";
import { EmployeeForm } from '../../components/Employee';
import { EmployeeProfile } from '../../components/Employee';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

const EmployeeManagement: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  
  // Modal states
  const [isFormModalVisible, setIsFormModalVisible] = useState(false);
  const [isProfileModalVisible, setIsProfileModalVisible] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [pwdModalVisible, setPwdModalVisible] = useState(false);
  const [pwdEmployee, setPasswordEmployee] = useState<Employee | null>(null);
  const [pwdForm] = Form.useForm();
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

  // Fetch employees
  const fetchEmployees = async () => {
    console.log("🔍 Fetching employees...");
    try {
      setLoading(true);
      const response = await apiService.getEmployees();
      if (response.success) {
        console.log("✅ Employees fetched:", response.data);
        
        let employeeData = response.data || [];
        
        // Ensure it's always an array
        if (!Array.isArray(employeeData)) {
          if (employeeData.employees) {
            employeeData = employeeData.employees;
          } else if (employeeData.data) {
            employeeData = employeeData.data;
          } else {
            employeeData = [];
          }
        }
        
        const finalEmployeeData = Array.isArray(employeeData) ? employeeData : [];
        setEmployees(finalEmployeeData);
        setFilteredEmployees(finalEmployeeData);
      } else {
        setEmployees([]);
        setFilteredEmployees([]);
      }
    } catch (error) {
      console.error("❌ Fetch error:", error);
      message.error('Failed to fetch employees');
      setEmployees([]);
      setFilteredEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  // Filter employees based on search and filters
  useEffect(() => {
    let filtered = employees;

    // Search filter
    if (searchText) {
      filtered = filtered.filter(emp => 
        emp.first_name.toLowerCase().includes(searchText.toLowerCase()) ||
        emp.last_name.toLowerCase().includes(searchText.toLowerCase()) ||
        emp.employee_id.toLowerCase().includes(searchText.toLowerCase()) ||
        emp.email.toLowerCase().includes(searchText.toLowerCase()) ||
        emp.department.toLowerCase().includes(searchText.toLowerCase()) ||
        emp.position.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Department filter
    if (departmentFilter) {
      filtered = filtered.filter(emp => emp.department === departmentFilter);
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter(emp => emp.status === statusFilter);
    }

    setFilteredEmployees(filtered);
  }, [employees, searchText, departmentFilter, statusFilter]);

  // Get unique departments for filter
  const departments = Array.from(new Set(employees.map(emp => emp.department))).filter(Boolean);

  // Handle employee actions
  const handleAddEmployee = () => {
    setEditingEmployee(null);
    setIsFormModalVisible(true);
  };

  const handleEditEmployee = (employee: Employee) => {
    setEditingEmployee(employee);
    setIsFormModalVisible(true);
  };

  const handleSetPassword = async (values: any) => {
    if (!pwdEmployee) return;
    try {
      const r = await apiService.setEmployeePassword(pwdEmployee.id, values.password, values.is_temp !== false);
      if (r.success) {
        message.success(r.message || 'Password set successfully');
        setPwdModalVisible(false);
        pwdForm.resetFields();
      } else {
        message.error(r.error || 'Failed to set password');
      }
    } catch {
      message.error('Failed to set password');
    }
  };

  const handleViewEmployee = (employee: Employee) => {
    setSelectedEmployee(employee);
    setIsProfileModalVisible(true);
  };

  const handleDeleteEmployee = async (employeeId: string) => {
    try {
      console.log("🗑️ Deleting employee ID:", employeeId);
      const response = await apiService.deleteEmployee(employeeId);
      if (response.success) {
        console.log("✅ Employee deleted successfully");
        message.success('Employee deleted successfully');
        fetchEmployees();
      } else {
        console.error("❌ Delete failed:", response.error);
        message.error(response.error || 'Failed to delete employee');
      }
    } catch (error) {
      console.error("❌ Delete error:", error);
      message.error('Failed to delete employee');
    }
  };

  // Export Employees to PDF Function
  const exportEmployeesToPDF = () => {
    console.log("📊 Exporting employees to PDF...");
    const tenantInfo = {
      name: "Your Company Name",
      logo: "🏢",
      primaryColor: "#1890ff"
    };
    
    const printContent = `
      <html>
        <head>
          <title>Employee Directory - ${tenantInfo.name}</title>
          <style>
            body { font-family: "Segoe UI", sans-serif; margin: 20px; }
            .letterhead { text-align: center; border-bottom: 3px solid ${tenantInfo.primaryColor}; padding-bottom: 20px; margin-bottom: 30px; }
            .company-name { font-size: 24px; color: ${tenantInfo.primaryColor}; font-weight: bold; }
            .report-title { font-size: 20px; margin: 10px 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { background: ${tenantInfo.primaryColor}; color: white; padding: 12px 8px; text-align: left; }
            td { padding: 10px 8px; border-bottom: 1px solid #f0f0f0; }
            tr:nth-child(even) { background-color: #fafafa; }
            .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #999; }
          </style>
        </head>
        <body>
          <div class="letterhead">
            <div class="company-name">${tenantInfo.name}</div>
            <div class="report-title">Employee Directory</div>
            <div>Generated on: ${new Date().toLocaleDateString()}</div>
          </div>
          <div>
            <h3>Total Employees: ${filteredEmployees.length}</h3>
            <table>
              <thead>
                <tr>
                  <th>Employee ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Department</th>
                  <th>Position</th>
                  <th>Employment Type</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                ${filteredEmployees.map(emp => `
                  <tr>
                    <td>${emp.employee_id}</td>
                    <td>${emp.first_name} ${emp.last_name}</td>
                    <td>${emp.email}</td>
                    <td>${emp.department || "N/A"}</td>
                    <td>${emp.position || "N/A"}</td>
                    <td>${emp.employment_type || "N/A"}</td>
                    <td>${emp.status || "Active"}</td>
                  </tr>
                `).join("")}
              </tbody>
            </table>
          </div>
          <div class="footer">
            <p>${tenantInfo.name} | Employee Directory Report</p>
            <p>Generated on ${new Date().toLocaleString()}</p>
          </div>
        </body>
      </html>
    `;
    
    const printWindow = window.open("", "_blank", "width=1200,height=800");
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => { 
        printWindow.print(); 
        console.log("✅ Export completed");
      }, 500);
    } else {
      message.error('Failed to open print window');
    }
  };

  const handleFormSuccess = () => {
    setIsFormModalVisible(false);
    setEditingEmployee(null);
    fetchEmployees();
  };

  // Table columns
  const columns = [
    {
      title: 'Employee',
      key: 'employee',
      render: (record: Employee) => (
        <Space>
          <Avatar 
            size={40} 
            icon={<UserOutlined />}
            style={{ backgroundColor: '#1890ff' }}
          >
            {record.first_name[0]}{record.last_name[0]}
          </Avatar>
          <div>
            <div style={{ fontWeight: 'bold' }}>
              {record.first_name} {record.last_name}
            </div>
            <div style={{ color: '#666', fontSize: '12px' }}>
              {record.employee_id}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (record: Employee) => (
        <Space direction="vertical" size="small">
          <Space size="small">
            <MailOutlined style={{ color: '#1890ff' }} />
            <span style={{ fontSize: '12px' }}>{record.email}</span>
          </Space>
          {record.phone && (
            <Space size="small">
              <PhoneOutlined style={{ color: '#52c41a' }} />
              <span style={{ fontSize: '12px' }}>{record.phone}</span>
            </Space>
          )}
        </Space>
      ),
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      render: (department: string) => (
        <Tag color="blue">{department}</Tag>
      ),
    },
    {
      title: 'Position',
      dataIndex: 'position',
      key: 'position',
    },
    {
      title: 'Employment Type',
      dataIndex: 'employment_type',
      key: 'employment_type',
      render: (type: string) => (
        <Tag color={type === 'full_time' ? 'green' : 'orange'}>
          {type === 'full_time' ? 'Full Time' : 'Part Time'}
        </Tag>
      ),
    },
    {
      title: 'Salary',
      dataIndex: 'basic_salary',
      key: 'basic_salary',
      render: (salary: string) => `₦${parseFloat(salary).toLocaleString()}`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Hire Date',
      dataIndex: 'hire_date',
      key: 'hire_date',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: Employee) => (
        <Space>
          <Tooltip title="View Profile">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewEmployee(record)}
            />
          </Tooltip>
          <Tooltip title="Edit Employee">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditEmployee(record)}
            />
          </Tooltip>
          <Tooltip title="Set Password">
            <Button
              type="text"
              icon={<LockOutlined />}
              onClick={() => handleSetPassword(record)}
            />
          </Tooltip>
          <Tooltip title="Set Bank Account">
            <Button
              type="text"
              icon={<BankOutlined />}
              onClick={() => {
                (window as any).__nexus_bank_employee = record;
                message.info('Open Payroll module to set up bank transfer for ' + record.first_name);
              }}
            />
          </Tooltip>
          <Tooltip title="Set Password">
            <Button
              type="text"
              icon={<KeyOutlined />}
              onClick={() => { setPasswordEmployee(record); pwdForm.resetFields(); setPwdModalVisible(true); }}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this employee?"
            onConfirm={() => handleDeleteEmployee(record.id?.toString() || record.employee_id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete Employee">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: '24px' }}>
          <Title level={2}>Employee Management</Title>
          <p style={{ color: '#666' }}>
            Manage employee records, profiles, and information
          </p>
        </div>

        {/* Filters and Actions */}
        <div style={{ 
          marginBottom: '24px', 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <Space size="middle">
            <Search
              placeholder="Search employees..."
              allowClear
              style={{ width: 300 }}
              onSearch={setSearchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            
            <Select
              placeholder="Filter by Department"
              allowClear
              style={{ width: 200 }}
              value={departmentFilter}
              onChange={setDepartmentFilter}
            >
              {departments.map(dept => (
                <Option key={dept} value={dept}>{dept}</Option>
              ))}
            </Select>

            <Select
              placeholder="Filter by Status"
              allowClear
              style={{ width: 150 }}
              value={statusFilter}
              onChange={setStatusFilter}
            >
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </Space>

          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddEmployee}
            >
              Add New Employee
            </Button>
            
            <Button
              icon={<FileTextOutlined />}
              onClick={exportEmployeesToPDF}
            >
              Export Employees
            </Button>
          </Space>
        </div>

        {/* Employee Table */}
        <Table
          columns={columns}
          dataSource={filteredEmployees}
          rowKey="id"
          loading={loading}
          pagination={{
            total: filteredEmployees.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} employees`,
          }}
          scroll={{ x: 1200 }}
        />

        {/* Employee Form Modal */}
        <Modal
          title={editingEmployee ? "Edit Employee" : "Add New Employee"}
          open={isFormModalVisible}
          onCancel={() => setIsFormModalVisible(false)}
          footer={null}
          width={800}
          destroyOnClose
        >
          <EmployeeForm
            employee={editingEmployee}
            onSuccess={handleFormSuccess}
            onCancel={() => setIsFormModalVisible(false)}
          />
        </Modal>

        {/* Employee Profile Modal */}
        <Modal
          title="Employee Profile"
          open={isProfileModalVisible}
          onCancel={() => setIsProfileModalVisible(false)}
          footer={null}
          width={600}
          destroyOnClose
        >
          {selectedEmployee && (
            <EmployeeProfile employee={selectedEmployee} />
          )}
        </Modal>
      </Card>

      {/* Set Employee Password Modal */}
      <Modal
        title={`Set Password — ${pwdEmployee?.first_name || ''} ${pwdEmployee?.last_name || ''}`}
        open={pwdModalVisible}
        onCancel={() => { setPwdModalVisible(false); pwdForm.resetFields(); }}
        footer={null}
        width={480}
      >
        <Form form={pwdForm} layout="vertical" onFinish={handleSetPassword}>
          <Form.Item name="password" label="New Password"
            rules={[{ required: true, message: 'Password required' }, { min: 6, message: 'Minimum 6 characters' }]}>
            <Input.Password placeholder="Enter password" />
          </Form.Item>
          <Form.Item name="confirm" label="Confirm Password"
            rules={[{ required: true }, ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) return Promise.resolve();
                return Promise.reject('Passwords do not match');
              }
            })]}>
            <Input.Password placeholder="Confirm password" />
          </Form.Item>
          <Form.Item name="is_temp" label="Password Type" initialValue={true}>
            <Select>
              <Select.Option value={true}>Temporary — employee must change on first login</Select.Option>
              <Select.Option value={false}>Permanent — no reset required</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">Set Password</Button>
              <Button onClick={() => { setPwdModalVisible(false); pwdForm.resetFields(); }}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

    </div>
  );
};

export default EmployeeManagement;
